import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { NumberParam, StringParam, useQueryParam } from 'use-query-params';
import Skeleton from 'react-loading-skeleton';
import { useTheme } from 'styled-components';
import { Button, SearchComponent, Select, TopProgress } from '../../../../components';
import { BodyDefault, HeadLine1 } from '../../../../components/typography/styled';
import { billingService, clientService } from '../../../../api';
import { useUi } from '../../../../context/ui.context';
import { usePageTitle } from '../../../../context/navigationContext';
import { getApiErrorMessage } from '../helpers';
import {
  Container,
  Header,
  ProductCard,
  ProductCardTitle,
  ProductDescription,
  ProductMeta,
  ProductsGrid,
  ProductTag,
  FormCard,
  FormStack,
  TitleWrapper,
} from '../styles';

const DESCRIPTION_MAX_LENGTH = 170;

const getClientDisplay = (client:any) => {
  const fullName = [client.first_name, client.last_name].filter(Boolean).join(' ');
  const fullNameWithEmail = fullName && client.email ? `${fullName} (${client.email})` : fullName;
  return client.display || client.name || fullNameWithEmail || client.email || `${client.id}`;
};

const OrdersCreate: FC = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const theme:any = useTheme();
  const { viewAlert } = useUi();
  usePageTitle(t('ORDER_ADDITIONAL_SERVICES'));

  const [page] = useQueryParam('page', NumberParam);
  const [pageSize] = useQueryParam('page_size', NumberParam);
  const [ordering] = useQueryParam('ordering', StringParam);
  const [searchString] = useQueryParam('search', StringParam);
  const [selectedClient, setSelectedClient] = useQueryParam('client', NumberParam);
  const [selectedUser, setSelectedUser] = useQueryParam('user', NumberParam);
  const [products, setProducts] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [selectedClientData, setSelectedClientData] = useState<any>();
  const [clientSearchString, setClientSearchString] = useState<string>('');
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [startingProduct, setStartingProduct] = useState<any>();

  const getUserDisplay = (user:any) => user.display || user.full_name || user.email || user.username || user.name || user.id;

  const clientOptions = useMemo(() => {
    const options = clients.map((client:any) => ({
      id: client.id,
      value: client.id,
      label: getClientDisplay(client),
    }));

    if (selectedClientData && !options.some((option:any) => option.id?.toString() === selectedClientData.id?.toString())) {
      options.push({
        id: selectedClientData.id,
        value: selectedClientData.id,
        label: getClientDisplay(selectedClientData),
      });
    }

    return options;
  }, [clients, selectedClientData]);

  const userOptions = useMemo(() => {
    return users.map((user:any) => ({
      id: user.id,
      value: user.id,
      label: getUserDisplay(user),
    }));
  }, [users]);

  useEffect(() => {
    let isActive = true;

    clientService.getClientList({ page: 1, pageSize: 100, ordering: 'name', search: clientSearchString })
      .then((res:any) => {
        if (!isActive) return;

        const data = res.data?.object || res.data;
        setClients(data?.objects || data?.results || []);
      })
      .catch((error:any) => {
        if (!isActive) return;

        viewAlert({
          severity: 'error',
          message: getApiErrorMessage(error, t('ERROR_OCCURRED')),
        });
      });

    return () => {
      isActive = false;
    };
  }, [clientSearchString, t, viewAlert]);

  useEffect(() => {
    if (!selectedClient) {
      setSelectedClientData(undefined);
      return;
    }

    if (selectedClientData?.id?.toString() === selectedClient?.toString()) {
      return;
    }

    const clientData = clients.find((client:any) => client.id?.toString() === selectedClient?.toString());
    if (clientData) {
      setSelectedClientData(clientData);
      return;
    }

    let isActive = true;
    clientService.getClient(`${selectedClient}`)
      .then((res:any) => {
        if (isActive) {
          setSelectedClientData(res.data);
        }
      })
      .catch(() => {
        if (isActive) {
          setSelectedClientData(undefined);
        }
      });

    return () => {
      isActive = false;
    };
  }, [clients, selectedClient, selectedClientData?.id]);

  useEffect(() => {
    if (!selectedClient) {
      setUsers([]);
      setSelectedUser(undefined);
      return;
    }

    setUsersLoading(true);
    clientService.getClientUsers(`${selectedClient}`, 100)
      .then((res:any) => {
        const data = res.data?.users || res.data?.objects || res.data?.results || [];
        const activeUsers = data.filter((user:any) => !user.invitation);
        setUsers(activeUsers);

        if (!activeUsers.length) {
          setSelectedUser(undefined);
          return;
        }

        if (!activeUsers.some((user:any) => user.id?.toString() === selectedUser?.toString())) {
          setSelectedUser(activeUsers[0].id);
        }
      })
      .catch((error:any) => {
        setUsers([]);
        setSelectedUser(undefined);
        viewAlert({
          severity: 'error',
          message: getApiErrorMessage(error, t('ERROR_OCCURRED')),
        });
      })
      .finally(() => setUsersLoading(false));
  }, [selectedClient, setSelectedUser, t, viewAlert]);

  const startStaffOrder = async (product:any) => {
    if (!selectedClient) {
      viewAlert({ severity: 'error', message: t('PLEASE_SELECT_CLIENT') });
      return;
    }
    if (!selectedUser) {
      viewAlert({ severity: 'error', message: t('PLEASE_SELECT_USER') });
      return;
    }

    const productIdentifier = product.code || product.id;
    setStartingProduct(productIdentifier);
    try {
      const currentSelectedClientData = selectedClientData?.id?.toString() === selectedClient?.toString()
        ? selectedClientData
        : clients.find((client:any) => client.id?.toString() === selectedClient?.toString());
      const clientData = currentSelectedClientData?.currency
        ? currentSelectedClientData
        : (await clientService.getClient(`${selectedClient}`)).data;
      const currency = clientData?.currency || clientData?.default_currency || clientData?.credits?.[0]?.currency;

      if (!currency) {
        viewAlert({ severity: 'error', message: t('CLIENT_CURRENCY_MISSING') });
        return;
      }

      const query = new URLSearchParams({
        client: `${selectedClient}`,
        user: `${selectedUser}`,
      });
      const cartData = {
        client: selectedClient,
        client_id: selectedClient,
        user: selectedUser,
        user_id: selectedUser,
        currency,
        currency_id: currency,
      };

      await billingService.startCartOrder({
        ...cartData,
        cart_data: cartData,
      });
      navigate(`/orders/create/order-product/${productIdentifier}?${query.toString()}`);
    } catch (error:any) {
      viewAlert({
        severity: 'error',
        message: getApiErrorMessage(error, t('ERROR_OCCURRED')),
      });
    } finally {
      setStartingProduct(undefined);
    }
  };

  useEffect(() => {
    setLoading(true);
    billingService.getProducts({
      page: page || 1,
      page_size: pageSize || 100,
      ordering: ordering || 'name',
      search: searchString || '',
    })
      .then((res:any) => {
        setProducts(res.data?.objects || res.data?.results || res.data?.object?.objects || res.data?.object?.results || []);
      })
      .catch((error:any) => {
        viewAlert({
          severity: 'error',
          message: getApiErrorMessage(error, t('ERROR_OCCURRED')),
        });
      })
      .finally(() => setLoading(false));
  }, [page, pageSize, ordering, searchString, t, viewAlert]);

  const productCards = useMemo(() => {
    return products.map((product:any) => {
      const description = product.description && product.description.length > DESCRIPTION_MAX_LENGTH
        ? `${product.description.slice(0, DESCRIPTION_MAX_LENGTH)}...`
        : product.description;

      return (
        <ProductCard key={product.id || product.code}>
          <div>
            <ProductCardTitle>{product.name}</ProductCardTitle>
            <ProductDescription>{description || t('NO_DESCRIPTION')}</ProductDescription>
            <ProductMeta>
              {product.group?.name ? <ProductTag>{product.group.name}</ProductTag> : null}
              {product.status ? <ProductTag>{product.status}</ProductTag> : null}
              {product.price_model_display || product.price_model ? (
                <ProductTag>{product.price_model_display || product.price_model}</ProductTag>
              ) : null}
            </ProductMeta>
          </div>
          <Button
            title={t('ORDER_PRODUCT')}
            variant='primary'
            size='display'
            loading={startingProduct === (product.code || product.id)}
            disabled={!selectedClient || !selectedUser || usersLoading || Boolean(startingProduct)}
            customStyles={{marginTop: '18px', justifyContent: 'center'}}
            onClick={() => startStaffOrder(product)}
          />
        </ProductCard>
      );
    });
  }, [products, selectedClient, selectedUser, usersLoading, startingProduct, t]);

  return (
    <Container>
      <Header>
        <TitleWrapper>
          <HeadLine1>{t('ORDER_ADDITIONAL_SERVICES')}</HeadLine1>
          <BodyDefault>{t('ORDER_PRODUCT_SELECT_HINT')}</BodyDefault>
        </TitleWrapper>
      </Header>
      <FormCard style={{maxWidth: '760px', marginBottom: '18px'}}>
        <FormStack>
          <Select
            title={t('CLIENT') as string}
            data={clientOptions}
            selectedValue={selectedClient}
            onChange={(clientId) => {
              setSelectedClientData(clients.find((client:any) => client.id?.toString() === clientId?.toString()));
              setSelectedClient(clientId);
              setSelectedUser(undefined);
            }}
            placeholder={t('SELECT_CLIENT')}
            hasSearch
            onSearchChange={setClientSearchString}
            required
          />
          <Select
            title={t('USER') as string}
            data={userOptions}
            selectedValue={selectedUser}
            onChange={setSelectedUser}
            disabled={!selectedClient || usersLoading || !userOptions.length}
            required
          />
          {selectedClient && !usersLoading && !userOptions.length ? (
            <BodyDefault>{t('NO_USERS')}</BodyDefault>
          ) : null}
        </FormStack>
      </FormCard>
      <div style={{maxWidth: '520px'}}>
        <SearchComponent />
      </div>
      {loading ? (
        <div style={{marginTop: '18px'}}>
          <TopProgress loading={loading} />
          <ProductsGrid>
            {Array.from({length: 6}, (_, index) => (
              <Skeleton
                key={index}
                baseColor={theme.palette[300]}
                highlightColor={theme.palette[20]}
                height={210}
              />
            ))}
          </ProductsGrid>
        </div>
      ) : productCards.length > 0 ? (
        <ProductsGrid>{productCards}</ProductsGrid>
      ) : (
        <BodyDefault style={{marginTop: '18px'}}>{t('NO_PRODUCTS')}</BodyDefault>
      )}
    </Container>
  );
};

export default OrdersCreate;
